import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { LogOut } from "lucide-react";
import { Bell, Menu, Plus, X, LayoutDashboard, ShieldCheck, Languages, Sun, Moon } from "lucide-react";
import { useTheme } from "@/context/Theme";
import { Logo } from "./Logo";
import { useI18n } from "@/i18n/I18nProvider";
import { useMe } from "@/context/AuthContext";
import { api } from "@/lib/api";

const NAV = [
  ["buy", "/search?purpose=sale"], ["rent", "/search?purpose=rent"], ["land", "/search?category=land"],
  ["wanted", "/wanted"], ["offices", "/offices"],
];

function NotificationBell() {
  const { me } = useMe();
  const { pathname } = useLocation();
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    if (me) api.get("/notifications/unread-count").then((r) => setUnread(r.data.unread)).catch(() => {});
  }, [me, pathname]);
  if (!me) return null;
  return (
    <Link to="/dashboard?tab=notifications" data-testid="header-notifications-link" className="relative hidden sm:block p-2 text-navy dark:text-white/85 hover:text-gold dark:hover:text-gold-light">
      <Bell className="w-5 h-5" />
      {unread > 0 && <span data-testid="header-unread-count" className="absolute -top-0.5 -end-0.5 min-w-[18px] h-[18px] rounded-full bg-gold text-[10px] font-bold text-white grid place-items-center">{unread}</span>}
    </Link>
  );
}

function UserMenu({ me }) {
  const { signOut } = useMe();
  const { t } = useI18n();
  return (
    <span data-testid="header-user-button" className="hidden md:flex items-center gap-1">
      <Link to="/dashboard" data-testid="header-avatar-link" title={me.email} className="w-9 h-9 rounded-full overflow-hidden bg-gold/20 grid place-items-center text-navy font-bold">
        {me.avatar ? <img src={me.avatar} alt="" referrerPolicy="no-referrer" className="w-full h-full object-cover" /> : (me.name || me.email || "?").slice(0, 1).toUpperCase()}
      </Link>
      <button onClick={signOut} data-testid="header-signout-btn" title={t("nav.logout")} className="p-2 text-ink-muted hover:text-red-600"><LogOut className="w-4 h-4" /></button>
    </span>
  );
}

export default function Header() {
  const { t, toggle, lang } = useI18n();
  const { me, isStaff, signOut } = useMe();
  const { theme, toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const { pathname, search } = useLocation();
  useEffect(() => { setOpen(false); }, [pathname, search]);

  const links = NAV.map(([k, to]) => (
    <NavLink key={k} to={to} data-testid={`nav-link-${k}`} className="px-3 py-2 text-sm font-medium text-navy dark:text-white/85 hover:text-gold dark:hover:text-gold-light transition-colors">
      {t(`nav.${k}`)}
    </NavLink>
  ));

  return (
    <header data-testid="site-header" style={{ paddingTop: "env(safe-area-inset-top)" }} className="header-bg sticky top-0 z-[1000] border-b border-line dark:border-gold/30 shadow-sm">
      <div className="mx-auto max-w-7xl px-3 sm:px-4 h-16 flex items-center gap-2 sm:gap-3">
        <Link to="/" data-testid="brand-logo-link" className="shrink-0"><Logo /></Link>
        <nav className="hidden lg:flex items-center ms-4">{links}</nav>
        <div className="ms-auto flex items-center gap-1.5">
          <button onClick={toggle} data-testid="lang-toggle-btn" className="flex items-center gap-1 rounded-full border border-line dark:border-white/20 px-3 h-9 text-xs font-bold text-navy dark:text-white hover:border-gold">
            <Languages className="w-4 h-4" /><span className="hidden sm:inline">{lang === "ar" ? "English" : "عربي"}</span><span className="sm:hidden">{lang === "ar" ? "EN" : "ع"}</span>
          </button>
          <button onClick={toggleTheme} data-testid="theme-toggle-btn" aria-label={t(theme === "dark" ? "theme.light" : "theme.dark")} title={t(theme === "dark" ? "theme.light" : "theme.dark")}
            className="grid place-items-center w-9 h-9 rounded-full border border-line dark:border-white/20 text-navy dark:text-gold-light hover:border-gold">
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <NotificationBell />
          {me && <Link to="/dashboard" data-testid="header-dashboard-link" title={t("nav.dashboard")} className="hidden sm:block p-2 text-navy dark:text-white/85 hover:text-gold dark:hover:text-gold-light"><LayoutDashboard className="w-5 h-5" /></Link>}
          {isStaff && <Link to="/admin" data-testid="header-admin-link" title={t("nav.admin")} className="hidden sm:block p-2 text-navy dark:text-white/85 hover:text-gold dark:hover:text-gold-light"><ShieldCheck className="w-5 h-5" /></Link>}
          {!me && (
            <Link to="/sign-in" data-testid="login-account-btn" className="hidden sm:inline-flex h-9 items-center rounded-full px-4 text-sm font-semibold text-navy dark:text-white hover:text-gold">{t("nav.login")}</Link>
          )}
          {me && <UserMenu me={me} />}
          <Link to="/add-property" data-testid="header-add-property-btn" className="btn-gold h-9 px-4 hidden md:inline-flex"><Plus className="w-4 h-4" />{t("nav.add")}</Link>
          <button className="lg:hidden p-2 text-navy dark:text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label={t("nav.menu")}>
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="header-bg lg:hidden border-t border-line dark:border-white/10 px-4 py-3 flex flex-col" data-testid="mobile-menu">
          {links}
          {me && <Link to="/dashboard" data-testid="mobile-dashboard-link" className="px-3 py-2 text-sm text-navy dark:text-white/85">{t("nav.dashboard")}</Link>}
          {isStaff && <Link to="/admin" data-testid="mobile-admin-link" className="px-3 py-2 text-sm text-navy dark:text-white/85">{t("nav.admin")}</Link>}
          {!me && <Link to="/sign-in" data-testid="mobile-login-link" className="px-3 py-2 text-sm text-navy dark:text-white/85">{t("nav.login")}</Link>}
          {me && (
            <button onClick={signOut} data-testid="mobile-signout-btn" className="flex items-center gap-2 px-3 py-2 text-sm text-red-700 dark:text-red-400"><LogOut className="w-4 h-4" />{t("nav.logout")}</button>
          )}
          <Link to="/add-property" data-testid="mobile-add-property-btn" className="btn-gold mt-2"><Plus className="w-4 h-4" />{t("nav.add")}</Link>
        </div>
      )}
    </header>
  );
}
